"use client"

/**
 * Leo spotted stack in a table popover. The info icon opens the same cards
 * as the sheet; primary actions open the question sheet to finish the work.
 */

import * as React from "react"

import { LeoSpottedQueueCard } from "@/components/question-quality/leo-spotted-queue-card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tip } from "@/components/ui/tip"
import {
  notCheckedLeoSpottedCard,
  type LeoSpottedCard,
} from "@/lib/question-quality/leo-spotted-queue"
import {
  readLeoSpotted,
  type LeoSpottedSubject,
} from "@/lib/question-quality/leo-spotted-source"
import { useLeoSpottedQueue } from "@/lib/question-quality/use-leo-spotted-queue"

export function LeoSpottedTablePopover({
  subject,
  stopRowClick = false,
  tipContentClassName,
  onOpenSheet,
}: {
  subject: LeoSpottedSubject
  stopRowClick?: boolean
  tipContentClassName?: string
  /** Opens the question sheet when the author picks a card action. */
  onOpenSheet?: () => void
}) {
  const [open, setOpen] = React.useState(false)
  const reading = readLeoSpotted(subject)
  const queueIssues = React.useMemo(
    () =>
      reading.issues.map((issue) => ({
        ...issue,
        instruction: issue.instruction ?? issue.label,
      })),
    [reading.issues],
  )
  const spottedQueue = useLeoSpottedQueue(subject.id, {
    issues: queueIssues,
    bloom: subject.bloom,
    difficulty: subject.difficulty,
    timesUsed: subject.timesUsed,
    pbi: subject.pbi,
    categoryIds: subject.categoryIds,
    categoryUnassigned: subject.categoryUnassigned,
    resolvedCount: reading.resolvedCount,
    resolvedIssues: reading.resolvedIssues,
    edited: reading.edited,
    tag:
      reading.tag === "needs-fixing" || reading.tag === "weak-performance"
        ? "needs-fixing"
        : reading.tag === "worth-a-look"
          ? "worth-a-look"
          : null,
  })

  const notChecked =
    queueIssues.length === 0 &&
    !reading.tag &&
    reading.resolvedCount === 0 &&
    !reading.edited

  const separationReady =
    subject.pbi != null && Number.isFinite(subject.pbi)

  const displayQueue = notChecked
    ? {
        cards: [notCheckedLeoSpottedCard("idle", separationReady)],
        activeIndex: 0,
      }
    : spottedQueue.queue

  const stop = stopRowClick
    ? (event: React.SyntheticEvent) => event.stopPropagation()
    : undefined

  function runFromPopover(card: LeoSpottedCard) {
    if (card.primaryLabel || card.primaryAskLeo) {
      setOpen(false)
      onOpenSheet?.()
    }
  }

  if (displayQueue.cards.length === 0) {
    return null
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tip
        label="Leo spotted"
        side="top"
        triggerClassName="inline-flex"
        contentClassName={tipContentClassName}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Leo spotted"
            aria-expanded={open}
            onClick={stop}
            onPointerDown={stop}
          >
            <i className="fa-light fa-circle-info text-xs" aria-hidden="true" />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent
        align="start"
        className="z-[80] w-[min(24rem,calc(100vw-2rem))] border-0 bg-transparent p-0 shadow-none"
        onClick={stop}
        onPointerDown={stop}
      >
        <LeoSpottedQueueCard
          queue={displayQueue}
          tag={reading.tag}
          onNavigate={notChecked ? () => {} : spottedQueue.navigate}
          onPrimary={runFromPopover}
          onSkip={spottedQueue.skip}
          onReopen={spottedQueue.reopen}
        />
      </PopoverContent>
    </Popover>
  )
}
